import { BookingDTOTransportType } from '@/clients/booking'

import { FormContainerItemDTO } from './types'

export type ContainerLocationSide = 'pickup' | 'delivery'

// useWatch hands back a deep partial, and only the location fields are read here
type ContainerLocationFields = Partial<
  Pick<FormContainerItemDTO, 'pickUpLocationID' | 'deliveryLocationID' | 'pickUpLocationIDs' | 'deliveryLocationIDs'>
>

/**
 * LCL has no container rows, so its addresses stay in the route details section.
 */
export const areAddressesOnContainerRows = (
  isFlagEnabled: boolean,
  transportType: BookingDTOTransportType | undefined
): boolean => isFlagEnabled && transportType === BookingDTOTransportType.Fcl

/**
 * The requested dates hang off the container rows only where the addresses already do, so a
 * booking that still enters one address pair keeps its single date pair as well.
 */
export const areRequestedDatesOnContainerRows = ({
  areAddressesOnContainers,
  isRequestedDatesEnabled,
}: {
  areAddressesOnContainers: boolean
  isRequestedDatesEnabled: boolean
}): boolean => areAddressesOnContainers && isRequestedDatesEnabled

/**
 * A row has an address on a side when either the shared row field or any of its per-container
 * entries names a location. The row's requested date opens on that.
 */
export const hasContainerLocation = (container: ContainerLocationFields, side: ContainerLocationSide): boolean => {
  const [rowKey, arrayKey] =
    side === 'pickup'
      ? (['pickUpLocationID', 'pickUpLocationIDs'] as const)
      : (['deliveryLocationID', 'deliveryLocationIDs'] as const)

  return container[rowKey] != null || (container[arrayKey] ?? []).some((locationID) => locationID != null)
}

/**
 * One address is enough for the matching door leg, so it counts as soon as any
 * container names a location.
 */
export const hasAnyContainerLocation = (
  containers: ContainerLocationFields[] | undefined,
  side: ContainerLocationSide
): boolean => (containers ?? []).some((container) => hasContainerLocation(container, side))

/**
 * The first location any container names on that side. The backend splits the door waypoint
 * into one door per container location, but it needs one door waypoint to start from.
 */
export const firstContainerLocation = (
  containers: ContainerLocationFields[] | undefined,
  side: ContainerLocationSide
): string | undefined => {
  const [rowKey, arrayKey] =
    side === 'pickup'
      ? (['pickUpLocationID', 'pickUpLocationIDs'] as const)
      : (['deliveryLocationID', 'deliveryLocationIDs'] as const)

  for (const container of containers ?? []) {
    const locationID = container[rowKey] ?? (container[arrayKey] ?? []).find((value) => value != null)
    if (locationID != null) {
      return locationID
    }
  }

  return undefined
}

/**
 * Every distinct location the containers name on that side, in the order they first appear.
 */
export const distinctContainerLocations = (
  containers: ContainerLocationFields[] | undefined,
  side: ContainerLocationSide
): string[] => {
  const [rowKey, arrayKey] =
    side === 'pickup'
      ? (['pickUpLocationID', 'pickUpLocationIDs'] as const)
      : (['deliveryLocationID', 'deliveryLocationIDs'] as const)
  const locationIDs = (containers ?? []).flatMap((container) => [container[rowKey], ...(container[arrayKey] ?? [])])

  return [...new Set(locationIDs.filter((locationID): locationID is string => locationID != null))]
}

export type PerContainerResize<T> =
  | { type: 'collapseToRow'; value: T | null }
  | { type: 'truncate'; values: (T | null)[] }
  | { type: 'keep' }

/**
 * What a per-container array (addresses or requested dates) should become when the container
 * quantity changes.
 *
 * A single unit has no per-container inputs and no toggle to switch back, so its value moves
 * to the row field. A quantity of 0 means the field is mid-edit, not that the containers are
 * gone, so the values are left alone rather than truncated away.
 */
export const resizePerContainerValues = <T>(current: (T | null)[], quantity: number): PerContainerResize<T> => {
  if (quantity === 1) {
    return { type: 'collapseToRow', value: current[0] ?? null }
  }
  if (quantity > 1 && current.length > quantity) {
    return { type: 'truncate', values: current.slice(0, quantity) }
  }
  return { type: 'keep' }
}

/**
 * The per-container array to start from when the user asks for a value per container. With the
 * requested dates on the rows, the row value goes into every unit so only the ones that differ
 * need editing; otherwise it goes into the first unit only, as before the dates.
 */
export const splitRowValuePerContainer = <T>({
  rowValue,
  quantity,
  copyToEveryUnit,
}: {
  rowValue: T | null | undefined
  quantity: number
  copyToEveryUnit: boolean
}): (T | null)[] =>
  Array.from({ length: Math.max(0, quantity) }, (_, index) =>
    copyToEveryUnit || index === 0 ? (rowValue ?? null) : null
  )
